/**
 * Centralized registry for ALL question-related React Query keys.
 *
 * No raw strings should be used for question cache keys anywhere in the
 * codebase.  Everything goes through these factories or predicates so that
 * adding a new list family is a one-line change.
 *
 * See: https://github.com/himeetball/webapp/issues/653
 */

import type { UserQuestionsFilter } from '@api-client/api-client';
import type { SortOption } from '@lib/sort';

export interface QuestionsQueryParams {
  skip?: number;
  limit?: number;
  // previously it was typed as string but consumer code used an array
  event_ids?: string[];
  event_id?: string;
}

// ── List / feed keys ────────────────────────────────────────────────────────

export const questionsBaseKey = () => ['questions'] as const;

export const questionsMeetverseKey = () => ['questions', 'meetverse'] as const;
export const questionsMeetverseInfiniteKey = () => ['questions', 'meetverse', 'infinite'] as const;

export const questionsParamsKey = (params?: QuestionsQueryParams) => {
  if (!params) return ['questions', {}] as const;

  const normalized = { ...params };
  if (params.event_id && !params.event_ids) {
    normalized.event_ids = [params.event_id];
    delete normalized.event_id;
  }

  return ['questions', normalized] as const;
};

export const questionsEventKey = (eventId: string) =>
  ['questions', { event_ids: [eventId] }] as const;

/**
 * Shared root of every event presence feed (all events, all sorts). The create path
 * (`useCreateQuestionMutation.onSuccess`) and the `space.question_created` SSE handler
 * invalidate THIS narrow root (never the broad `questionsBaseKey()`), so a new post
 * refreshes the mounted event feed without resweeping the Redis-served meetverse feed
 * before ARQ fan-out lands (the TECH-305 contract in `useCreateQuestionMutation`).
 */
export const questionsEventFeedRootKey = () => ['questions', 'event-feed'] as const;

/**
 * Authed event "presence feed" (questions by people Going, in the event's spaces).
 * Nested under `questionsEventFeedRootKey()` so that (a) `questionsBaseKey()`
 * invalidations sweep it and (b) `isQuestionListKey` treats it as a question list,
 * letting feed-card optimistic updates reach it for free.
 */
export const questionsEventFeedKey = (eventId: string, sort = 'recent') =>
  [...questionsEventFeedRootKey(), eventId, sort] as const;

/** Prefix (all sorts) for invalidating an event's whole presence feed in one call. */
export const questionsEventFeedBaseKey = (eventId: string) =>
  [...questionsEventFeedRootKey(), eventId] as const;

export const questionsSpaceKey = (spaceId: string) => ['questions', 'space', spaceId] as const;

/**
 * Full mounted key for the space-questions infinite feed.
 *
 * Membership is part of the key so the non-member PREVIEW feed and the full
 * MEMBER feed are DISTINCT cache entries: when a user joins and `isMember` flips,
 * React Query swaps to a fresh member-context query automatically — no
 * invalidation, no in-flight-dedupe race (the prod feed-stuck-on-preview bug).
 * `isMember` sits AFTER `spaceId` so `questionsSpaceKey(spaceId)` stays a valid
 * invalidation prefix for every existing caller (pin updates, realtime SSE,
 * create-question optimistic prepend, pull-to-refresh).
 */
export const spaceQuestionsFeedKey = (spaceId: string, isMember: boolean, topicParam?: string) =>
  [...questionsSpaceKey(spaceId), isMember, topicParam] as const;

/** Space-scoped list of pinned question IDs. Child of questionsSpaceKey — inherits cascade invalidation. */
export const pinnedQuestionsSpaceKey = (spaceId: string) =>
  [...questionsSpaceKey(spaceId), 'pinned'] as const;

// ── Detail / single-item keys ───────────────────────────────────────────────

/** Root of ALL id-keyed question-detail caches (['question', id] and its
 *  subkeys like suggested-helpers). Distinct from the plural ['questions']
 *  list root; family-wide operations that must reach mounted DETAIL screens
 *  (e.g. user.updated identity refresh) need BOTH roots. */
export const questionDetailBaseKey = () => ['question'] as const;

export const questionDetailKey = (questionId?: string) =>
  [...questionDetailBaseKey(), questionId] as const;

// Admin suggested-helpers for a question. Detail-scoped (under the `question`
// namespace) so it shares invalidation with the question it belongs to.
export const suggestedHelpersKey = (questionId: string) =>
  [...questionDetailBaseKey(), questionId, 'suggested-helpers'] as const;

export const questionTotalsKey = (params?: QuestionsQueryParams) =>
  ['questions', 'totals', params] as const;

export const questionMySpacesFeedKey = (serverSortBy?: SortOption) =>
  serverSortBy
    ? (['questions', 'my-spaces-feed', serverSortBy] as const)
    : (['questions', 'my-spaces-feed'] as const);

export const questionInfiniteKey = (isAuthenticated: boolean, event_ids?: string[]) =>
  ['questions', 'infinite', event_ids, isAuthenticated] as const;

export const userQuestionsBaseKey = () => ['user-questions'] as const;

interface UserQuestionsParams {
  skip?: number;
  limit?: number;
  filter?: UserQuestionsFilter;
}

export const userQuestionsKey = (userId: string, params?: UserQuestionsParams) =>
  params && Object.keys(params).length > 0
    ? (['user-questions', userId, params] as const)
    : (['user-questions', userId] as const);

export const resolveQuestionBaseKey = () => ['resolve-question'] as const;

export const resolveQuestionKey = (slug: string) => ['resolve-question', slug] as const;

/** Root of the UUID-lookup family below: the prefix that sweeps every uuid-routed post page. */
export const questionByUuidBaseKey = () => ['question-by-uuid'] as const;

/**
 * Private key for the UUID-lookup path in useQuestionBySlugOrId. Avoids
 * collision with generated hooks that write AxiosResponse to ['question', id]
 * (questionDetailKey's namespace).
 */
export const questionByUuidKey = (id: string) => [...questionByUuidBaseKey(), id] as const;

// ── Interaction keys ─────────────────────────────────────────────────────────

/** Root of the per-question interactions rosters (me-too / i-can-help rows,
 *  which render user names and avatars). Own root, NOT under ['question']. */
export const questionInteractionsBaseKey = () => ['questionInteractions'] as const;

// Single-arg by design: only the structured `/interactions` endpoint
// (`useGetQuestionInteractions`, returns `QuestionInteractionsResponse`) is
// registered against this key. The flat `/all_interactions` variant returns
// a different shape (`InteractionRead[]`) and must NOT share this cache slot —
// if it gets re-introduced, give it its own factory.
export const questionInteractionsKey = (questionId: string | undefined) =>
  [...questionInteractionsBaseKey(), questionId] as const;

/**
 * Params accepted by the `/interactions` endpoint and used as the
 * cache-discriminator. Mirrors the inline param shape on
 * `useReadInteractionsApiV1InteractionsGet` in `api-client.ts`. Typed (not
 * `unknown`) so producers and invalidators can't fragment the cache by
 * passing structurally-different objects without a type error.
 */
export interface InteractionsKeyParams {
  page?: number;
  per_page?: number;
  user_id?: string;
  other_user_id?: string;
  interaction_type?: string;
  target_type?: string;
  filter_out?: string;
}

export const interactionsKey = (params?: InteractionsKeyParams) =>
  params === undefined ? (['interactions'] as const) : (['interactions', params] as const);

// ── We Met v2 connection keys ────────────────────────────────────────────────

// Single unparameterized key by design: every consumer reads the full list and
// every v2 mutation optimistically updates THIS key. A status-filtered variant
// would silently miss those optimistic writes, so it must ship with its own
// cache-coherence handling rather than a bare key param.
export const weMetConnectionsKey = () => ['we-met-connections'] as const;

export const questionUpliftUsersKey = (questionId: string) =>
  ['questionUpliftUsers', questionId] as const;

export const meTooInteractionKey = (questionId: string) =>
  ['meTooInteraction', questionId] as const;

export const iCanHelpInteractionKey = (questionId: string | undefined) =>
  ['iCanHelpInteraction', questionId] as const;

// ── Predicates ───────────────────────────────────────────────────────────────

/**
 * Prefixes that identify question *list* caches (feed, profile, etc.).
 *
 * When you add a new list family (e.g. `['space-questions', …]`), append the
 * first element string to this array.  The optimistic-update helpers in
 * `operations.ts` and the centralized invalidation in `invalidate.ts` will
 * pick it up automatically.
 */
export const QUESTION_LIST_PREFIXES = ['questions', 'user-questions'] as const;

/**
 * Second-segment values under `['questions', ...]` that are NOT lists.
 *
 * `questionTotalsKey` returns `{ total: number }`, not a question collection;
 * including it in `isQuestionListKey` would needlessly sweep the totals cache
 * during optimistic snapshot/cancel passes. Add new non-list subkeys here as
 * they appear; new list families don't need to be enumerated.
 */
const NON_LIST_QUESTION_SUBKEYS = ['totals'] as const;

/**
 * Does this query key belong to a question list cache?
 *
 * Used by optimistic update snapshot/restore so that every list family
 * registered here is covered without hand-matching branches.
 */
export const isQuestionListKey = (key: readonly unknown[]): boolean => {
  const prefixIsQuestionKey = (QUESTION_LIST_PREFIXES as readonly string[]).includes(
    String(key[0]),
  );

  if (!prefixIsQuestionKey) return false;

  return !(NON_LIST_QUESTION_SUBKEYS as readonly string[]).includes(String(key[1]));
};

export const isQuestionDetailKey = (key: readonly unknown[]): boolean =>
  key[0] === 'question' && typeof key[1] === 'string';

export const isResolveQuestionKey = (key: readonly unknown[]): boolean =>
  key[0] === 'resolve-question' && typeof key[1] === 'string';

export const isQuestionInteractionsKey = (key: readonly unknown[]): boolean =>
  key[0] === 'questionInteractions' && typeof key[1] === 'string';

// ── Entity store keys ────────────────────────────────────────────────────────

/**
 * Canonical per-question entity key. This is the single source of truth for
 * a question's mutable state (counters, interaction flags). All optimistic
 * writes target this key; all list hooks subscribe to it via useQueries.
 */
export const questionEntityKey = (id: string) => ['question-entity', id] as const;

export const isQuestionEntityKey = (key: readonly unknown[]): boolean =>
  key[0] === 'question-entity' && typeof key[1] === 'string';
